import React, { useState } from 'react'
import classNames from 'classnames'
import { isEmpty } from 'lodash'
import validator from 'validator'
import { Scrollbars } from 'react-custom-scrollbars'
import { TickDate } from 'images/common';
import CustomInput from 'components/Input/CustomInput'
import Button from 'components/Button';
import { DateRange } from 'react-date-range';
import {
  subMinutes,
  subHours,
  subDays,
  subWeeks,
  subMonths,
  subYears,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths,
  differenceInYears,
  format,
  isSameDay
} from 'date-fns';
import {
  INTERVAL_DAY,
  INTERVAL_MINUTE,
  INTERVAL_HOUR,
  INTERVAL_WEEK,
  INTERVAL_MONTH,
  INTERVAL_YEAR
} from 'containers/CustomDashboard/Dashboard/details/chartConfigurations'

const dateErrorMessage = 'Invalid date format'

export const DatePicker = ({
  color = 'accent2',
  relativeTimeOptions = [...RELATIVE_TIME_RANGE_OPTIONS],
  selectedDate = {},
  onApplySelectedDate = (val) => null,
  hasDashboardDefaultOption = true
}) => {

  const [currentSelectedDate, setCurrentSelectedDate] = useState(isEmpty(selectedDate) ? getDefaultSelectedDate({ relativeTimeOptions: relativeTimeOptions }) : selectedDate)
  const [showCalendar, setShowCalendar] = useState(false)
  const [startError, setStartError] = useState(false)
  const [endError, setEndError] = useState(false)
  const [calendarRange, setCalendarRange] = useState({
    key: 'calendarRange',
    startDate: null,
    endDate: new Date(''),
  })

  const handleOnFocusInput = (event) => {
    setShowCalendar(true)
    // const getValue = event.target.value
    // const getName = event.target.name
    // if (getValue && isValidDateTime(getValue)) {
    //   getName === 'start' ? setStartError(false) : setEndError(false)
    // } else {
    //   getName === 'start' ? setStartError(true) : setEndError(true)
    // }
  }

  // const handleOnBlurInput = () => {
  //   setShowCalendar(false)
  // }

  const handleRelativeOptionSelection = (newRelativeOption) => {
    if (newRelativeOption && newRelativeOption.label === 'Dashboard default') {
      setShowCalendar(false)
      setCurrentSelectedDate({ ...newRelativeOption })
      setCalendarRange({
        ...calendarRange,
        startDate: null,
        endDate: new Date(''),
      })
      return
    }

    const start = getTimeSpan(newRelativeOption) || ''
    const end = getTimeSpan() || ''
    const updateSelected = {
      ...currentSelectedDate,
      label: newRelativeOption.label,
      start: start,
      end: end,
      mode: 'relative',
      timeInterval: newRelativeOption.timeInterval,
      timeQuantity: newRelativeOption.timeQuantity,
      isDefault: newRelativeOption.isDefault,
      ...('totalDays' in newRelativeOption ? { totalDays: newRelativeOption.totalDays } : {})
    }

    setStartInputValue(start)
    setEndInputValue(end)
    setShowCalendar(false)
    setCurrentSelectedDate(updateSelected)
    setCalendarRange({
      ...calendarRange,
      startDate: new Date(start),
      endDate: new Date(end),
    })
    resetInputError()
  }

  const handleOnChangeCalendarRange = (newRange) => {
    if (!newRange || !newRange.calendarRange) return

    const newCalendarRange = newRange.calendarRange
    const startDate = newCalendarRange.startDate
    let endDate = newCalendarRange.endDate

    if (!(startDate instanceof Date) || isNaN(startDate.getTime())) {
      return
    }

    if (!(endDate instanceof Date) || isNaN(endDate.getTime())) {
      endDate = new Date(startDate.getTime())
    } else {
      endDate = new Date(endDate.getTime())
    }

    endDate.setHours(23, 59, 59)

    const start = format(startDate, 'yyyy-MM-dd HH:mm:ss')
    const end = format(endDate, 'yyyy-MM-dd HH:mm:ss')
    const interval = calculateTimeInterval(startDate, endDate)

    const updatedSelectedDate = {
      label: `${interval.timeQuantity} ${interval.timeInterval}`,
      start,
      end,
      timeInterval: interval.timeInterval,
      timeQuantity: interval.timeQuantity,
      ...('totalDays' in interval ? { totalDays: interval.totalDays } : {})
    }

    setStartInputValue(start)
    setEndInputValue(end)
    resetInputError()
    setCalendarRange({ ...newCalendarRange })
    setCurrentSelectedDate({ ...updatedSelectedDate })
  }

  const handleOnApplyDate = () => {
    if (!currentSelectedDate.start || !currentSelectedDate.end) {
      return onApplySelectedDate(null)
    }
    onApplySelectedDate({ ...currentSelectedDate })
  }

  const handleOnResetDate = () => {
    const defaultDate = getDefaultSelectedDate({ relativeTimeOptions: relativeTimeOptions })

    const defaultStart = getTimeSpan(defaultDate) || ''
    const defaultEnd = getTimeSpan() || ''

    setShowCalendar(false)
    setCurrentSelectedDate({ ...defaultDate })
    setCalendarRange({
      ...calendarRange,
      startDate: null,
      endDate: new Date(''),
    })
    resetInputError()
    const updateDate = {
      ...defaultDate,
      start: defaultStart,
      end: defaultEnd,
      ...('totalDays' in defaultDate ? { totalDays: defaultDate.totalDays } : {})
    }

    if (!updateDate.start || !updateDate.end) {
      return onApplySelectedDate(null)
    }
    onApplySelectedDate({ ...updateDate })
  }

  const onInputChange = (event) => {
    const getValue = event.target.value
    const getName = event.target.name
    const [datePart, timePart] = getValue.split(' ')

    if (getValue && isValidDateTime(getValue)) {
      getName === 'start' ? setStartError(false) : setEndError(false)
    } else {
      getName === 'start' ? setStartError(true) : setEndError(true)
    }

    getName === 'start' ? setStartInputValue(getValue) : setEndInputValue(getValue)
  }

  const isValidDateTime = (value) => {
    const [datePart, timePart] = value.split(' ')
    return validator.isDate(datePart) && validator.isTime(timePart, { mode: 'withSeconds' })
  }

  const resetInputError = () => {
    setEndError(false)
    setStartError(false)
  }

  const onInputBlur = (event) => {
    const getValue = event.target.value
    const getName = event.target.name
    const startDate = getName === 'start' ? getValue : currentSelectedDate.start
    const endDate = getName === 'end' ? getValue : currentSelectedDate.end

    const calendarRange = {
      key: 'calendarRange',
      startDate: new Date(startDate),
      endDate: new Date(endDate)
    }

    getValue && !(endError || startError) && handleOnChangeCalendarRange({ calendarRange })
  }

  const getInputStartDate = () => {
    if (isOptionRelativeTime(currentSelectedDate)) {
      const { start } = getRelativeTimeDates(currentSelectedDate)
      return format(start, 'yyyy-MM-dd HH:mm:ss') || ''
    } else {
      return currentSelectedDate.start
    }
  }

  const getInputEndDate = () => {
    if (isOptionRelativeTime(currentSelectedDate)) {
      const { end } = getRelativeTimeDates(currentSelectedDate)
      return format(end, 'yyyy-MM-dd HH:mm:ss') || ''
    } else {
      return currentSelectedDate.end
    }
  }

  const [startInputValue, setStartInputValue] = useState(getInputStartDate());
  const [endInputValue, setEndInputValue] = useState(getInputEndDate());

  return (
    <div className={classNames('date-picker', showCalendar && 'date-picker--calendar')}>
      <div className='date-picker__dates'>
        <div className='absolute-time border-right'>
          <div className="date-picker__header">Absolute time range</div>
          <CustomInput
            value={startInputValue}
            onChange={onInputChange}
            onBlur={onInputBlur}
            onFocus={handleOnFocusInput}
            name="start"
            label={'From'}
            isInvalid={startError}
            hintText={dateErrorMessage}
            hideHint={!startError}
            customClass='mx-b-20'
          />
          <CustomInput
            value={endInputValue}
            onChange={onInputChange}
            onFocus={handleOnFocusInput}
            onBlur={onInputBlur}
            name="end"
            label={'To'}
            isInvalid={endError}
            hintText={dateErrorMessage}
            hideHint={!endError}
          />
          <div className="calendar_btn">
            <div>
              <Button
                onClick={handleOnApplyDate}
                className={`button button_${color}`}
                disabled={endError || startError}
              >
                Apply
              </Button>
            </div>
            <div>
              <button
                onClick={handleOnResetDate}
                className="calendar_btn_reset"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className='relative-time'>
          <div className="date-picker__header">Relative time ranges</div>
          <Scrollbars
            autoHeight
            autoHide
            className='relative-time__scrollbar'
            autoHideDuration={1}
            autoHeightMin={285}
            renderView={props => (
              <div
                {...props}
                style={{ ...props.style, overscrollBehavior: 'contain' }}
              />
            )}
          >
            {getDefaultDateOptions(hasDashboardDefaultOption, relativeTimeOptions).map((option, index) =>
              <div
                key={`${index}_${option.timeQuantity}`}
                onClick={() => handleRelativeOptionSelection(option)}
                className={classNames('relative-time__option', {
                  'relative-time__option_selected': isRelativeTimeSelected(currentSelectedDate, option)
                })}
              >
                {option.label}{isRelativeTimeSelected(currentSelectedDate, option) && <span><TickDate /></span>}
              </div>
            )}
          </Scrollbars>
        </div>
      </div>

      {showCalendar &&
        <div className='date-picker__calendar'>
          <div className={`calendar ${color}`}>
            <DateRange
              color={color}
              customClass={color}
              weekStartsOn={1}
              showPreview={true}
              dateDisplayFormat="d MMM yyyy"
              // onChange={item => handleOnChangeCalendarRange(item)}
              onChange={handleOnChangeCalendarRange}
              ranges={[calendarRange]}
              rangeColors={[color]}
            />
          </div>
        </div>
      }
    </div>
  )
}

export const RELATIVE_TIME_RANGE_OPTIONS = [
  { label: 'Dashboard default', start: '', end: '', timeInterval: '', timeQuantity: 0, isDefault: false },
  { label: 'Last 5 minutes', start: '', end: '', timeInterval: INTERVAL_MINUTE, timeQuantity: 5, isDefault: false },
  { label: 'Last 15 minutes', start: '', end: '', timeInterval: INTERVAL_MINUTE, timeQuantity: 15, isDefault: false },
  { label: 'Last 30 minutes', start: '', end: '', timeInterval: INTERVAL_MINUTE, timeQuantity: 30, isDefault: false },
  { label: 'Last 1 hour', start: '', end: '', timeInterval: INTERVAL_MINUTE, timeQuantity: 60, isDefault: false },
  { label: 'Last 3 hours', start: '', end: '', timeInterval: INTERVAL_MINUTE, timeQuantity: 180, isDefault: false },
  { label: 'Last 6 hours', start: '', end: '', timeInterval: INTERVAL_MINUTE, timeQuantity: 360, isDefault: true },
  { label: 'Last 12 hours', start: '', end: '', timeInterval: INTERVAL_MINUTE, timeQuantity: 720, isDefault: false },
  { label: 'Last 1 day', start: '', end: '', timeInterval: INTERVAL_DAY, timeQuantity: 1, isDefault: false },
  { label: 'Last 7 days', start: '', end: '', timeInterval: INTERVAL_DAY, timeQuantity: 7, isDefault: false },
  { label: 'Last 15 days', start: '', end: '', timeInterval: INTERVAL_DAY, timeQuantity: 15, isDefault: false },
  { label: 'Last 1 month', start: '', end: '', timeInterval: INTERVAL_MONTH, timeQuantity: 1, isDefault: false },
  { label: 'Last 3 months', start: '', end: '', timeInterval: INTERVAL_MONTH, timeQuantity: 3, isDefault: false },
  { label: 'Last 6 months', start: '', end: '', timeInterval: INTERVAL_MONTH, timeQuantity: 6, isDefault: false },
]

const getDefaultSelectedDate = ({ defaultDateOption = {}, relativeTimeOptions = [] }) => {
  const newDateByDefault = relativeTimeOptions.find(item => item.isDefault)
  return newDateByDefault
  // if (isEmpty(defaultDateOption)) {
  //   return newDateByDefault
  // } 
  // const defaultDate = RELATIVE_TIME_RANGE_OPTIONS.find(item => isEqual(item, defaultDate))  
  // return defaultDate || newDateByDefault
}

const calculateTimeInterval = (startDate, endDate) => {
  // TODO Fix the interval time (it loose days if for example I select 8 days, it turns to only 1 week)
  const intervals = [
    { intervalName: INTERVAL_YEAR, funcDiff: differenceInYears, minMinutes: 60 * 24 * 365 },
    { intervalName: INTERVAL_MONTH, funcDiff: differenceInMonths, minMinutes: 60 * 24 * 30 },
    { intervalName: INTERVAL_WEEK, funcDiff: differenceInWeeks, minMinutes: 60 * 24 * 7 },
    { intervalName: INTERVAL_DAY, funcDiff: differenceInDays, minMinutes: 60 * 24 },
    { intervalName: INTERVAL_HOUR, funcDiff: differenceInHours, minMinutes: 60 },
    { intervalName: INTERVAL_MINUTE, funcDiff: differenceInMinutes, minMinutes: 1 }
  ]

  const diffInMinutes = differenceInMinutes(endDate, startDate)
  const interval = intervals.find(interval => diffInMinutes >= interval.minMinutes)

  if (interval) {
    return { timeInterval: interval.intervalName, timeQuantity: interval.funcDiff(endDate, startDate) }
  }

  return { timeInterval: '', timeQuantity: 0 }
}

const isRelativeTimeSelected = (selectedTime, option) => {
  return option.label === selectedTime.label
}

export const getTimeSpan = (item) => {
  const now = new Date();
  let time = new Date();
  const timeInterval = item && typeof item.timeInterval === 'string' ? item.timeInterval : ''

  if (item && timeInterval.includes(INTERVAL_MINUTE)) {
    time = subMinutes(now, item.timeQuantity);
  }

  if (item && timeInterval.includes(INTERVAL_HOUR)) {
    time = subHours(now, item.timeQuantity);
  }

  if (item && timeInterval.includes(INTERVAL_DAY)) {
    time = subDays(now, item.timeQuantity);
  }

  if (item && timeInterval.includes(INTERVAL_WEEK)) {
    time = subWeeks(now, item.timeQuantity);
  }

  if (item && timeInterval.includes(INTERVAL_MONTH)) {
    time = subMonths(now, item.timeQuantity);
  }

  if (item && timeInterval.includes(INTERVAL_YEAR)) {
    time = subYears(now, item.timeQuantity);
  }

  // Format the result (optional, for readability)
  const formattedTime = format(time, 'yyyy-MM-dd HH:mm:ss');

  return formattedTime
}

export const isOptionRelativeTime = (option) => {
  return !isEmpty(option) && option.label && /\blast\b/i.test(option.label)
}

export const getRelativeTimeDates = (relativeTimeOption) => {
  const start = getTimeSpan(relativeTimeOption) || ''
  const end = getTimeSpan() || ''

  return { start, end }
}

export const getDefaultDateOptions = (withDashboardDefault = true, relativeTimeOptions = []) => {
  return withDashboardDefault ?
    [...relativeTimeOptions] :
    relativeTimeOptions.filter(option => option.label !== 'Dashboard default')
}